// What waits on a person in a trip, one rule each, wherever it's counted (DESIGN.md, Views, "What waits
// on me leads"): the server (get_trip's `unrated_by_you` and `waiting`, `query`'s `unrated_by: "me"`,
// Your trips' cards, the weekly round-up) and the web view (Ideas and its tab, People's "Not rated by
// you", Bookings' Waiting for you and its tab) all ask here, so their numbers can't disagree (until
// 2026-10-20 four places counted what waits on my rating four ways). The web view shows a choice's options
// waiting on my rating on Bookings and the rest on Ideas (web/src/plan.ts, `ratingWaits`); the server's
// counts take both. Plain functions over anything with an item's shape: the server's stored records and the
// web view's items alike.

// Someone's say on an item: only a tier counts as rating it.
export interface Said {
  person: string;
  tier: string | null;
}

export interface Rateable {
  type: string;
  status: string;
  interest: readonly Said[];
}

// What's rated: places and goals. Stays and moves are the plan, compared as options under a choice.
const RATED_TYPES: ReadonlySet<string> = new Set(["place", "goal"]);
// Still being shaped: an idea, an option under a choice, or planned. Never what's booked, done, dropped,
// not chosen or cancelled (nor skipped or missed).
const SHAPING: ReadonlySet<string> = new Set(["idea", "option", "planned"]);

// Whether it waits on this person's rating: a place or goal still being shaped, whoever added it, that
// they haven't given a tier. Saying only whether they're going doesn't count; a No does.
export function waitsOnRating(item: Rateable, person: string): boolean {
  return (
    RATED_TYPES.has(item.type) &&
    SHAPING.has(item.status) &&
    !item.interest.some((x) => x.person === person && Boolean(x.tier))
  );
}

// Everything waiting on this person's rating, in the order given.
export function waitingOnRating<T extends Rateable>(items: readonly T[], person: string): T[] {
  return items.filter((i) => waitsOnRating(i, person));
}

// An AI's suggestion still being considered: a place or a goal (a hotel or a way there it suggests is an
// option under a choice, compared on Bookings), until it's in the plan (planned, booked,
// done) or dropped: the map's quieter pin and get_trip's `waiting.suggestions_unrated_by_you`. Ideas'
// Suggestions lists more, whoever added it (web/src/suggestions.ts, `listedInSuggestions`; since 2026-10-23).
export function isSuggestion(item: { origin?: { kind: string } | undefined; type: string; status: string }): boolean {
  return (
    item.origin?.kind === "ai" && RATED_TYPES.has(item.type) && (item.status === "idea" || item.status === "option")
  );
}

// ---------- Friends' tips ----------

// A friend's tip waits to be brought in until an AI marks it taken: Ideas' "N new" on the friend's card
// (get_trip's `waiting.pad_entries_not_taken` counts its own read of them the same way). On a read-only trip none waits, since bringing one in is a
// write the server refuses (DESIGN.md's log, "Nothing waits on a read-only trip"); they count again once it
// can change.
export function tipsWaiting(entries: readonly { taken: boolean }[], readOnly: boolean): number {
  return readOnly ? 0 : entries.filter((e) => !e.taken).length;
}

// ---------- Due dates ----------

// A to-do: open while its status is "todo"; its owner (none: nobody's yet) and due date, a plain date.
export interface Dated {
  type: string;
  status: string;
  owner?: string | undefined;
  due?: string | undefined;
}

// Within this many days (today counting as 0), a due date is soon: it counts on Bookings' tab and Your
// trips' cards, and shows in the sun's colour.
export const DUE_SOON_DAYS = 7;
// How far ahead the weekly round-up looks for what's due (src/due-words.ts).
export const DUE_LATER_DAYS = 14;

const DAY_MS = 86_400_000;
const day = (date: string) => Date.parse(`${date.slice(0, 10)}T00:00:00Z`);

// How many days from today (a plain date, the person's own) to a due date: 0 today, -2 two days late.
export function daysUntil(due: string, today: string): number {
  return Math.round((day(due) - day(today)) / DAY_MS);
}

// An open to-do with a due date.
export function openDated<T extends Dated>(item: T): item is T & { due: string } {
  return item.type === "task" && item.status === "todo" && Boolean(item.due);
}

// Whose a to-do is now: its owner while they're in the trip (`members`: the ids of its members now). One
// who has left it (removed, gone, or their account deleted) leaves it nobody's, until someone takes it
// (DESIGN.md, Reminder; the lead's call, 2026-10-20: kept theirs, it reminded nobody and Bookings didn't
// count it). Decided when read: the stored owner stays as it was.
export function ownerNow(item: { owner?: string | undefined }, members: ReadonlySet<string>): string | undefined {
  return item.owner && members.has(item.owner) ? item.owner : undefined;
}

// Mine or nobody's (nobody's waits on everyone in the trip), wherever it's counted: Bookings' Waiting for
// you and its tab, Your trips, the due-date reminders and the round-up.
export function mineOrNobodys(item: Dated, person: string, members: ReadonlySet<string>): boolean {
  const owner = ownerNow(item, members);
  return !owner || owner === person;
}

// Whether a to-do waits on this person now: open, mine or nobody's, due within 7 days or overdue.
export function dueSoonFor(item: Dated, person: string, today: string, members: ReadonlySet<string>): boolean {
  return openDated(item) && mineOrNobodys(item, person, members) && daysUntil(item.due, today) <= DUE_SOON_DAYS;
}

// How many due dates (of what's mine or nobody's) are within 7 days or overdue.
export function dueSoonCount(dues: readonly string[], today: string): number {
  return dues.filter((d) => daysUntil(d, today) <= DUE_SOON_DAYS).length;
}

// How far a due date is, as people say it: "today", "tomorrow", "in 5 days", "1 day late".
export function dueIn(due: string, today: string): string {
  const left = daysUntil(due, today);
  if (left === 0) return "today";
  if (left === 1) return "tomorrow";
  if (left > 1) return `in ${left} days`;
  return left === -1 ? "1 day late" : `${-left} days late`;
}
